import { useEffect, useRef, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { logger } from '../lib/logger';
import { useAudioCleanup } from './useAudioCleanup';

interface WaveformOptions {
  container: HTMLElement;
  audioUrl: string;
  onReady?: () => void;
  onError?: (error: Error) => void;
}

export function useWaveform() {
  const wavesurfer = useRef<WaveSurfer | null>(null);
  const abortController = useRef<AbortController | null>(null);
  const retryTimeout = useRef<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSubscribed = useRef(true);
  const isLoadingRef = useRef(false);
  const { cleanup: cleanupAudio } = useAudioCleanup();

  const cleanup = useCallback(() => {
    // Set subscription flag first to prevent any new operations
    isSubscribed.current = false;

    // Clear any pending retries
    if (retryTimeout.current) {
      window.clearTimeout(retryTimeout.current);
      retryTimeout.current = null;
    }

    // Clean up audio resources
    cleanupAudio();

    // Destroy wavesurfer instance first
    if (wavesurfer.current) {
      try {
        // Remove all event listeners first
        wavesurfer.current.unAll();
        wavesurfer.current.destroy();
      } catch (e) {
        logger.warn('Error destroying WaveSurfer:', e);
      }
      wavesurfer.current = null;
    }

    // Only abort if there's an active loading operation AND no retries are pending
    if (isLoadingRef.current && !retryTimeout.current && abortController.current?.signal && !abortController.current.signal.aborted) {
      try {
        abortController.current.abort();
      } catch (e) {
        logger.warn('Error aborting request:', e);
      }
    }
    abortController.current = null;
    isLoadingRef.current = false;
  }, [cleanupAudio]);

  const initialize = useCallback(async ({ container, audioUrl, onReady, onError }: WaveformOptions) => {
    try {
      // Clean up existing instance first
      cleanup();
      
      // Reset subscription flag
      isSubscribed.current = true;
      setIsLoading(true);
      setError(null);

      // Validate URL before creating WaveSurfer instance
      try {
        new URL(audioUrl);
      } catch (e) {
        throw new Error('Invalid audio URL');
      }

      // Create new WaveSurfer instance with improved configuration
      wavesurfer.current = WaveSurfer.create({
        container,
        waveColor: 'rgba(56, 189, 248, 0.4)',
        progressColor: 'rgba(56, 189, 248, 0.8)',
        cursorColor: '#0ea5e9',
        barWidth: 2,
        barRadius: 3,
        cursorWidth: 1,
        height: 80,
        barGap: 2,
        normalize: true,
        partialRender: true,
        responsive: true,
        backend: 'MediaElement',
        xhr: {
          cache: 'force-cache',
          mode: 'cors',
          credentials: 'include',
          headers: [
            { key: 'Accept', value: 'audio/*' },
            { key: 'Cache-Control', value: 'no-cache' },
            { key: 'X-Client-Info', value: 'famous-artist-remastered@1.0.0' }
          ]
        }
      });

      // Set up event handlers with better error handling
      wavesurfer.current.once('ready', () => {
        if (!isSubscribed.current) return;
        setIsLoading(false);
        isLoadingRef.current = false;
        setError(null);
        onReady?.();
      });

      wavesurfer.current.once('error', (error: Error) => {
        if (!isSubscribed.current) return;
        const errorMessage = error?.message || 'Failed to load audio file';
        logger.error('WaveSurfer error:', { error: errorMessage });
        setError(errorMessage);
        isLoadingRef.current = false;
        onError?.(error);
      });

      // Load audio with retry mechanism
      const maxRetries = 3;
      const retryDelay = 1000;

      const loadAudio = async (retryCount = 0): Promise<void> => {
        if (!isSubscribed.current || !wavesurfer.current) return;

        try {
          // Create new AbortController for each attempt
          if (abortController.current) {
            abortController.current.abort();
          }
          abortController.current = new AbortController();
          isLoadingRef.current = true;

          // Load audio with signal and timeout
          const loadPromise = wavesurfer.current.load(audioUrl, undefined, abortController.current?.signal);
          const timeoutPromise = new Promise((_, reject) => {
            const timeoutId = setTimeout(() => {
              clearTimeout(timeoutId);
              reject(new Error('Loading timeout'));
            }, 30000);
          });

          await Promise.race([loadPromise, timeoutPromise]);
          
          if (!isSubscribed.current) return;
          setError(null);
        } catch (error) {
          if (!isSubscribed.current) return;

          if (error instanceof Error) {
            if (error.name === 'AbortError') {
              // Only log abort errors during cleanup
              logger.debug('Audio loading aborted');
              return;
            }

            logger.error('WaveSurfer load error:', error);
            setError(`Failed to load audio: ${error.message}`);

            // Retry with exponential backoff
            if (retryCount < maxRetries) {
              const delay = retryDelay * Math.pow(2, retryCount);
              retryTimeout.current = window.setTimeout(() => {
                if (isSubscribed.current) {
                  loadAudio(retryCount + 1);
                }
              }, delay);
            }
          }
        } finally {
          if (!isSubscribed.current) {
            isLoadingRef.current = false;
          }
        }
      };

      await loadAudio();
    } catch (error) {
      if (!isSubscribed.current) return;

      logger.error('WaveSurfer initialization error:', error);
      setError(error instanceof Error ? error.message : 'Failed to initialize waveform');
      onError?.(error instanceof Error ? error : new Error('Failed to initialize waveform'));
    }
  }, [cleanup]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    wavesurfer,
    isLoading,
    error,
    initialize,
    cleanup
  };
}